import { Capacitor } from '@capacitor/core'
import { SocialLogin } from './SocialLoginPlugin'
import { AccessToken } from './definitions'

const OAUTH_STATE_KEY = 'social_login_oauth_pending'

const parseJwt = (token: string): any => {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split('')
            .map((c) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            })
            .join('')
    )
    return JSON.parse(jsonPayload)
}
export interface IEntraMeResp {
    '@odata.context'?: string
    businessPhones?: any[]
    displayName?: string
    givenName?: string
    jobTitle?: null
    mail?: string
    mobilePhone?: string
    officeLocation?: null
    preferredLanguage?: null
    surname?: string
    userPrincipalName?: string
    id?: string
}
export interface EntraProviderResponse {
    accessToken?: AccessToken
    idToken?: string
    profile?: {
        email?: string
        givenName?: string
        familyName?: string
        id?: string
        name?: string
        imageUrl?: string
        mobilePhone?: string
    }
    responseType: 'online' | 'offline'
    serverAuthCode?: string
}
export const traditionalOAuth = async (
    provider: string,
    authorizeUrl: string,
    clientId: string,
    scopes: string[],
    redirectUri: string,
    loginType: 'online' | 'offline',
    nonce: string | null
): Promise<{ provider: string; result: EntraProviderResponse }> => {
    const uniqueScopes = [...new Set([...(scopes || []), 'openid'])]

    const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri!,
        response_type: loginType === 'offline' ? 'code' : 'id_token token',
        response_mode: 'fragment',
        scope: uniqueScopes.join(' '),
        ...(nonce && { nonce }),
        state: 'popup',
    })

    // Construct the authorization URL with the tenant ID
    const url = `${authorizeUrl}?${params.toString()}`

    const width = 500
    const height = 600
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2
    localStorage.setItem(OAUTH_STATE_KEY, 'true')

    let inAppBrowserRef: any = null
    let popup = null
    if (Capacitor.isNativePlatform()) {
        const resp = await SocialLogin.genericLogin({ url: url })

        return oauthResponseFromToken(resp.data, provider)
    } else {
        popup = window.open(
            url,
            'Entra ID Sign In',
            `width=${width},height=${height},left=${left},top=${top},popup=1`
        )
    }

    return new Promise((resolve, reject) => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data?.source === 'react-devtools-bridge') return

            if (event.data?.type === 'oauth-response') {
                window.removeEventListener('message', handleMessage)
                popup?.close()
                if (loginType === 'online') {
                    resolve(oauthResponseFromToken(event.data.data, provider))
                } else {
                    const { serverAuthCode } = event.data as {
                        serverAuthCode: string
                    }
                    resolve({
                        provider: provider,
                        result: {
                            responseType: 'offline',
                            serverAuthCode,
                        },
                    })
                }
            }
        }
        window.addEventListener('message', handleMessage)
        // Timeout after 5 minutes
        setTimeout(() => {
            window.removeEventListener('message', handleMessage)
            if (Capacitor.isNativePlatform()) {
                inAppBrowserRef?.close()
            } else {
                popup?.close()
            }
            reject(new Error('OAuth timeout'))
        }, 300000)
    })
}

const oauthResponseFromToken = (tokens: any, provider: string) => {
    const { accessToken, idToken } = tokens
    if (accessToken && idToken) {
        const profile = parseJwt(idToken)

        return {
            provider: provider,
            result: {
                accessToken: {
                    token: accessToken,
                },
                idToken,
                profile: {
                    email: profile.email || profile.preferred_username || null,
                    familyName: profile.family_name || null,
                    givenName: profile.given_name || null,
                    id: profile.oid || profile.sub || null,
                    name: profile.name || null,
                    imageUrl: null, // Entra ID doesn't provide image URLs in tokens
                },
                responseType: 'online' as 'online',
            },
        }
    }
}
const generateNonce = (): string => {
    const array = new Uint32Array(10)
    window.crypto.getRandomValues(array)
    return Array.from(array, (dec) => ('0' + dec.toString(16)).slice(-2)).join(
        ''
    )
}

export const entraLogin = async (): Promise<any> => {
    const resp = await traditionalOAuth(
        'entra',
        'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
        'bad81977-84cc-4d19-9492-3828d507b848',
        ['openid', 'https://graph.microsoft.com/User.Read'],
        'https://pr.d-i.eventr.softpauer.com/oauth/',
        'online',
        generateNonce()
    )

    // fetch user info
    const userInfoResp: IEntraMeResp = await (
        await fetch('https://graph.microsoft.com/v1.0/me', {
            headers: {
                Authorization: `Bearer ${resp.result.accessToken?.token}`,
            },
        })
    ).json()

    resp.result.profile = {
        email: userInfoResp.mail || userInfoResp.userPrincipalName || undefined,
        familyName: userInfoResp.surname || undefined,
        givenName: userInfoResp.givenName || undefined,
        id: userInfoResp.id || undefined,
        name: userInfoResp.displayName || undefined,
        mobilePhone:
            userInfoResp.mobilePhone ||
            userInfoResp.businessPhones?.[0] ||
            null,
        imageUrl: undefined, // Entra ID doesn't provide image URLs in tokens
    }

    return resp
}
